import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MaleProfile from "../assets/img/profile-man.png"
import FemaleProfile from "../assets/img/profile-woman.png"
import '../styles/Profile.css';

const Profile = () => {
    const [userInfo, setUserInfo] = useState(null);
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [ordersError, setOrdersError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuthStatus = () => {
            const userStatus = localStorage.getItem('loggedIn');
            const userData = localStorage.getItem('userInfo');

            if (userStatus !== 'true' || !userData) {
                navigate('/login');
                return;
            }

            try {
                const parsedUserInfo = JSON.parse(userData);
                setUserInfo(parsedUserInfo);
            } catch (error) {
                console.error('Error parsing user info:', error);
                navigate('/login');
            } finally {
                setIsLoading(false);
            }
        };

        checkAuthStatus();
    }, [navigate]);

    useEffect(() => {
        if (userInfo) {
            fetchOrders();
        }
    }, [userInfo]);

    const fetchOrders = async () => {
        try {
            setOrdersLoading(true);
            setOrdersError(null);

            // Try to get token from various possible locations
            const token = localStorage.getItem('userToken') ||
                (userInfo.token ? userInfo.token : null);

            // Prepare headers
            const headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            };

            // Add authorization header if token exists
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch('https://akadgatepharmacy.com/backend/public/api/v1/orders', {
                method: 'GET',
                headers: headers,
                credentials: 'include'
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Authentication failed. Please login again.');
                }
                throw new Error(`Failed to fetch orders. Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setOrders(result.data);
            } else {
                throw new Error(result.message || 'Failed to load orders');
            }
        } catch (err) {
            setOrdersError(err.message);
            console.error('Error fetching orders:', err);
        } finally {
            setOrdersLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(price));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleRetryOrders = () => {
        fetchOrders();
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!userInfo) {
        return null; // Will redirect to login
    }

    return (
        <div className="profile-page">
            {/* Profile Content */}
            <div className="profile container section-padding">
                <div className="row">
                    <div className="col-md-4">
                        <div className="user-info p-4">
                            <div className="user-info-img">
                                <img
                                    src={!userInfo.gender ? MaleProfile : FemaleProfile}
                                    alt="ProfileImage"
                                    height="128"
                                    width="128"
                                />
                            </div>
                            <div className="user-info-data">
                                <h6 className="text-main-muted text-capitalize">
                                    <span className="fw-bold">Name: </span> {userInfo.name}
                                </h6>
                                <h6 className="text-main-muted">
                                    <span className="fw-bold">Phone: </span> {userInfo.phone}
                                </h6>
                                <h6 className="text-main-muted">
                                    <span className="fw-bold">Email: </span> {userInfo.email}
                                </h6>
                                <h6 className="text-main-muted text-capitalize">
                                    <span className="fw-bold">Address: </span> {userInfo.address}
                                </h6>
                                <h6 className="text-main-muted">
                                    <span className="fw-bold">Gender: </span> {userInfo.gender ? "Female" : "Male"}
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="user-details p-4">
                            <h4 className="text-color-bright mb-4">Order History</h4>

                            {ordersLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading orders...</span>
                                    </div>
                                    <p className="text-main-muted mt-2">Loading your orders...</p>
                                </div>
                            ) : ordersError ? (
                                <div className="alert alert-warning">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        <span>Unable to load orders: {ordersError}</span>
                                    </div>
                                    <button
                                        onClick={handleRetryOrders}
                                        className="btn btn-sm btn-outline-primary mt-2"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : orders.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="no-orders-icon mb-3" style={{fontSize: '3rem'}}>📦</div>
                                    <h5 className="text-main-muted">No Orders Yet</h5>
                                    <p className="text-muted">You haven't placed any orders yet.</p>
                                    <button
                                        onClick={() => navigate('/products')}
                                        className="btn btn-primary"
                                    >
                                        Start Shopping
                                    </button>
                                </div>
                            ) : (
                                <div className="profile-orders-list">
                                    {orders.map((order) => (
                                        <div key={order.id} className="profile-order-card mb-4">
                                            <div className="order-header d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <h6 className="mb-1 fw-bold text-color-bright">
                                                        Order #{order.invoiceNumber}
                                                    </h6>
                                                    <small className="text-muted">
                                                        {formatDate(order.date)} • {order.paymentMethod}
                                                    </small>
                                                </div>
                                                <div className="text-end">
                                                    <span className={`badge ${order.status === 'Pay' ? 'bg-success' : 'bg-warning'} mb-2`}>
                                                        {order.status}
                                                    </span>
                                                    <div className="fw-bold text-color-bright">
                                                        ${formatPrice(order.totalPrice)}
                                                    </div>
                                                </div>
                                            </div>

                                            {order.note && (
                                                <div className="order-note alert alert-info py-2 mb-3">
                                                    <small>
                                                        <strong>Note: </strong>{order.note}
                                                    </small>
                                                </div>
                                            )}

                                            <div className="order-items">
                                                {order.sell_orders.map((item) => (
                                                    <div key={item.id} className="order-item d-flex align-items-center py-2 border-bottom">
                                                        <div className="item-image me-3">
                                                            <img
                                                                src={`${item.product.featuredImage.replace('/storage', 'https://akadgatepharmacy.com/backend/storage/app/public')}`}
                                                                alt={item.product.name}
                                                                className="rounded"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                onError={(e) => {
                                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0VDRUNFIi8+CjxwYXRoIGQ9Ik0yNS41IDM1LjVWMzkuNUMzMC4xODY2IDM5LjUgMzQuNSAzNS4xODY2IDM0LjUgMzAuNUMzNC41IDI1LjgxMzQgMzAuMTg2NiAyMS41IDI1LjUgMjEuNVYyNS41QzI4LjUzMTcgMjUuNSAzMSAyNy45NjgzIDMxIDMxQzMxIDM0LjAzMTcgMjguNTMxNyAzNi41IDI1LjUgMzYuNVoiIGZpbGw9IiNDRUNFQ0UiLz4KPC9zdmc+';
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="item-details flex-grow-1">
                                                            <h6 className="mb-1 fw-semibold">{item.product.name}</h6>
                                                            <small className="text-muted d-block">{item.product.category}</small>
                                                            <small className="text-muted">Qty: {parseFloat(item.qty).toFixed(0)}</small>
                                                        </div>
                                                        <div className="item-pricing text-end">
                                                            <div className="fw-semibold">${formatPrice(item.price)}</div>
                                                            <small className="text-muted">
                                                                Total: ${formatPrice(parseFloat(item.price) * parseFloat(item.qty))}
                                                            </small>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="order-footer d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                                <small className="text-muted">
                                                    {parseFloat(order.totalQty).toFixed(0)} items
                                                </small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;